import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { PrinterpreferenceService } from './printerpreference.service';
import { PrintRichTextMessage, RichTextLine } from '../models/ns-tray-rich-text.models';

/** How long to wait for NS Tray's print-result or print-error. */
const PRINT_REPLY_TIMEOUT_MS = 60_000;
/** NS Tray from before print replies: a label is taken as printed after this long, as it always was. */
const LEGACY_PRINT_TIMEOUT_MS = 4000;

interface PrintJob {
  id: string;
  lines: RichTextLine[];
  printer: string;
  done: (printed: boolean) => void;
}

export interface NsTrayAlert {
  message: string;
  type: 'success' | 'error' | 'warning';
}

@Injectable({ providedIn: 'root' })
export class NsTrayPrintService {
  availablePrinters: string[] = [];
  selectedPrinter = '';
  isConnected = false;

  private socket?: WebSocket;
  private queue: PrintJob[] = [];
  private current: PrintJob | null = null;
  private isPrinting = false;
  private nextJobId = 1;
  /** This NS Tray answers every print (it says so with the printer list). */
  private printReplies = false;

  /**
   * This NS Tray can print barcode lines. It came with print replies, so they go together; an
   * older NS Tray prints a barcode line as empty text.
   */
  get supportsBarcodes(): boolean {
    return this.printReplies;
  }
  private printTimeout: any = null;
  private hasAlerted = false;
  /** NS Tray has sent its printer list on the current connection. */
  private printersReceived = false;
  private readonly printers$ = new Subject<void>();

  /** Fires when printers arrived but no saved printer matched — the UI should prompt. */
  readonly needsPrinterSelection$ = new Subject<void>();
  /** Connection / print problems for the UI to surface. */
  readonly alerts$ = new Subject<NsTrayAlert>();

  constructor(private prefs: PrinterpreferenceService) {}

  /** Idempotent: opens the NS Tray socket if not already open. */
  connect(): void {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) return;
    if (this.socket) {
      // Replacing a socket that is closing: nothing sent on it can be answered any more.
      this.isConnected = false;
      this.printersReceived = false;
      if (this.current || this.queue.length > 0) this.raiseDisconnectAlert();
      this.failPendingJobs();
    }
    const socket = new WebSocket('ws://localhost:8282');

    socket.onopen = () => {
      if (this.socket !== socket) return;
      this.isConnected = true;
      this.hasAlerted = false;
      socket.send(JSON.stringify({ type: 'get-printers' }));
    };

    socket.onmessage = (event) => {
      if (this.socket !== socket) return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'printers' && Array.isArray(data.printers)) {
          this.printReplies = data.printReplies === true;
          this.availablePrinters = data.printers;
          const pref = this.prefs.getPrinterPreference();
          if (pref && this.availablePrinters.includes(pref)) {
            this.selectedPrinter = pref;
          } else {
            this.needsPrinterSelection$.next();
          }
          this.printersReceived = true;
          this.printers$.next();
        }
        if (data.type === 'print-error') {
          this.alerts$.next({ message: `Label not printed: ${data.message ?? 'unknown error'}`, type: 'error' });
        }
        if (
          data.type === 'print-result' ||
          data.type === 'print-error' ||
          data.type === 'error' ||
          data.success !== undefined
        ) {
          // A reply naming another job is late: that job already timed out. Replies without an
          // id (older NS Tray) are for the job being printed.
          if (data.jobId === undefined || data.jobId === this.current?.id) {
            this.onJobComplete(data.type !== 'print-error' && data.type !== 'error' && data.success !== false);
          }
        }
      } catch {
        /* ignore malformed frames */
      }
    };

    // A socket replaced by a newer one (it was still closing when connect() ran again) must
    // leave the new connection and its labels alone.
    socket.onerror = () => {
      if (this.socket !== socket) return;
      this.isConnected = false;
      this.printersReceived = false;
      this.raiseDisconnectAlert();
    };

    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.isConnected = false;
      this.printersReceived = false;
      this.raiseDisconnectAlert();
      this.failPendingJobs();
      setTimeout(() => this.connect(), 5000);
    };

    this.socket = socket;
  }

  /**
   * Resolves true once NS Tray is connected and has sent its printer list (so the printer and
   * whether it can print barcodes are known), or false after `timeoutMs` without it.
   */
  whenReady(timeoutMs = 5000): Promise<boolean> {
    if (this.printersReceived) return Promise.resolve(true);
    this.connect();
    return new Promise(resolve => {
      const sub = this.printers$.subscribe(() => { clearTimeout(timer); sub.unsubscribe(); resolve(true); });
      const timer = setTimeout(() => { sub.unsubscribe(); resolve(false); }, timeoutMs);
    });
  }

  /** Persist + use a printer chosen by the user. */
  setPrinter(name: string): void {
    this.selectedPrinter = name;
    this.prefs.setPrinterPreference(name);
  }

  /**
   * Enqueue one label. Serialised to avoid jamming the printer. Resolves true when NS Tray
   * reports it printed, false when it failed or NS Tray didn't answer within a minute. An older
   * NS Tray that never replies gets the old behaviour: printed after 4 s.
   */
  print(lines: RichTextLine[]): Promise<boolean> {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.alerts$.next({ message: 'NS Tray not connected — cannot print.', type: 'warning' });
      return Promise.resolve(false);
    }
    if (!this.selectedPrinter) {
      this.alerts$.next({ message: 'No printer selected.', type: 'warning' });
      return Promise.resolve(false);
    }
    return new Promise<boolean>(done => {
      this.queue.push({ id: String(this.nextJobId++), lines, printer: this.selectedPrinter, done });
      this.processNext();
    });
  }

  private processNext(): void {
    if (this.isPrinting || this.queue.length === 0) return;
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      // Socket dropped between enqueue and dispatch — surface it and fail the waiting jobs, so
      // callers can offer a reprint. Avoids a thrown send() on a closed socket wedging the queue.
      this.alerts$.next({ message: 'NS Tray not connected — cannot print.', type: 'warning' });
      this.failPendingJobs();
      return;
    }
    this.isPrinting = true;
    const job = this.queue.shift()!;
    this.current = job;
    clearTimeout(this.printTimeout);
    if (this.printReplies) {
      // A save-as-PDF dialog or a present-on-demand printer can take a while; silence for a
      // minute is not a confirmation, so the label counts as not printed (Reprint offers it again).
      this.printTimeout = setTimeout(() => {
        this.alerts$.next({ message: "NS Tray didn't confirm a label within a minute. Check it printed; if not, use Reprint.", type: 'warning' });
        this.onJobComplete(false);
      }, PRINT_REPLY_TIMEOUT_MS);
    } else {
      // An NS Tray that never replies (not yet updated on this PC): the old behaviour.
      this.printTimeout = setTimeout(() => this.onJobComplete(true), LEGACY_PRINT_TIMEOUT_MS);
    }
    const payload: PrintRichTextMessage = {
      type: 'print-rich-text',
      printer: job.printer,
      lines: job.lines,
      jobId: job.id,
    };
    this.socket.send(JSON.stringify(payload));
  }

  private onJobComplete(printed: boolean): void {
    clearTimeout(this.printTimeout);
    this.current?.done(printed);
    this.current = null;
    this.isPrinting = false;
    setTimeout(() => this.processNext(), 500);
  }

  /** The connection dropped: nothing waiting will print, so report every job as failed. */
  private failPendingJobs(): void {
    clearTimeout(this.printTimeout);
    for (const job of [this.current, ...this.queue]) job?.done(false);
    this.current = null;
    this.queue = [];
    this.isPrinting = false;
  }

  private raiseDisconnectAlert(): void {
    if (this.hasAlerted) return;
    this.hasAlerted = true;
    this.alerts$.next({
      message: 'NS Tray disconnected. Please check the connection.',
      type: 'warning',
    });
  }
}
